
'use client';
import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { FaChevronDown } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { useUser } from '@/providers/userContext';
import { getLogo, getStoreName } from "@/services/storeService";
import SearchBar from '../themes/SearchBar';
// import name from '../components/images/zemnewlogo.png';

// Define interfaces for type safety
interface Category {
  id: number;
  name: string;
  seo_url: string;
  parent_id: number;
  sortorder: number;
}

interface User {
  name: string;
}

interface CartInfo {
  cartcount?: number;
}

interface UserContext {
  user: User | null;
  cartinfoData: CartInfo | null;
}

interface NavbarProps {
  categoryListData: Category[] | null;
}

const Navbar = ({ categoryListData }: NavbarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false); // Sidebar state
  const [selected, setSelected] = useState('');
  const [wishlist, setWishlist] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const { user, cartinfoData } = useUser() as UserContext;
  const [userData, setUserData] = useState<User | null>(user);
  console.log(userData, "userData")
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Dropdown state for mobile

  useEffect(() => {
    setUserData(user);
  }, [user]);

  useEffect(() => {
    setCartCount(cartinfoData?.cartcount ?? 0);
  }, [cartinfoData]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    if (isDropdownOpen) setIsDropdownOpen(false); // Close dropdown when sidebar opens
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSelect = (category: string) => {
    setSelected(category);
  };

  const handleNavigate = (path: string) => {
    router.push(path);
    setIsDropdownOpen(false); // Close dropdown on navigation
  };

  const toggleSearchBar = () => {
    setShowSearchBar(!showSearchBar);
    if (isDropdownOpen) setIsDropdownOpen(false); // Close dropdown when search bar opens
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleNavigate(`/products?search=${encodeURIComponent(searchQuery)}`);
  };

  const getSubcategoriesByParentId = (parentId: number): Category[] => {
    return categoryListData?.filter((category) => category.parent_id === parentId) ?? [];
  };

  const mainCategories = categoryListData
    ?.sort((a, b) => a.sortorder - b.sortorder)
    .filter((category) => category.parent_id === 0)
    ?.sort((a, b) => a.id - b.id) ?? [];

  return (
    <>
      <div className="!sticky !top-0 !z-50 !bg-white ">
        <div className="!flex !items-center !justify-between !px-4 !py-4 lg:!px-10 xl:!px-16">
          {/* Hamburger Icon (Visible on lg and below) */}
          <div className="!block xl:!hidden !w-4/12">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" onClick={toggleSidebar} className="lucide lucide-menu-icon lucide-menu"><path d="M4 5h16" /><path d="M4 12h16" /><path d="M4 19h16" /></svg>

          </div>

          {/* Sidebar (Visible on lg and below when open) */}
          <div
            className={`!fixed !top-0 !left-0 !h-full !bg-white !shadow-lg !z-[1000] !transition-transform !duration-300 !ease-in-out ${isOpen ? '!translate-x-0' : '!-translate-x-full'
              } !w-64 xl:!hidden`}
          >
            <button
              className="!text-primary !text-2xl !p-4"
              onClick={toggleSidebar}
            >
              ×
            </button>

            <div className="!px-4 !py-3">
              <div className="!flex !items-center !gap-3 !mb-3">
                <Link href="/signin" className="!flex !items-center !gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 13C14.396 13 16.575 13.694 18.178 14.672C18.978 15.16 19.662 15.736 20.156 16.362C20.642 16.977 21 17.713 21 18.5C21 19.345 20.589 20.011 19.997 20.486C19.437 20.936 18.698 21.234 17.913 21.442C16.335 21.859 14.229 22 12 22C9.771 22 7.665 21.86 6.087 21.442C5.302 21.234 4.563 20.936 4.003 20.486C3.41 20.01 3 19.345 3 18.5C3 17.713 3.358 16.977 3.844 16.361C4.338 15.736 5.021 15.161 5.822 14.671C7.425 13.695 9.605 13 12 13ZM12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7C17 8.32608 16.4732 9.59785 15.5355 10.5355C14.5979 11.4732 13.3261 12 12 12C10.6739 12 9.40215 11.4732 8.46447 10.5355C7.52678 9.59785 7 8.32608 7 7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2Z"
                      fill="#000"
                    />
                  </svg>
                  <span
                    className="!text-primary"
                    style={{
                      display: userData !== null ? 'none' : 'flex',
                      alignItems: 'end',
                    }}
                  >
                    Login
                  </span>
                  <span
                    className="!text-primary"
                    style={{
                      display: userData !== null ? 'flex' : 'none',
                      alignItems: 'end',
                    }}
                  >
                    {userData?.name}
                  </span>
                </Link>
              </div>

              <h3 className="!font-semibold !text-lg !mb-3">Products</h3>
              <div className="!py-3">
                {mainCategories.slice(0, 9).map((mainCategory) => (
                  <div key={mainCategory.id} className="!pb-3">
                    <a
                      href={`/products/${mainCategory.seo_url}`}
                      className="!text-primary !font-normal"
                    >
                      {mainCategory.name}
                    </a>
                  </div>
                ))}
              </div>

              <h3
                className="!font-semibold !text-lg !mb-3"
                style={{ display: userData !== null ? 'flex' : 'none' }}
              >
                MY Account
              </h3>
              {userData !== null && (
                <ul className="!flex !flex-col">
                  <li className="!pb-4">
                    <a href="/my-account" className="!text-primary">
                      My Account
                    </a>
                  </li>
                  <li className="!pb-4">
                    <a href="/myprofile" className="!text-primary">
                      My Profile
                    </a>
                  </li>
                  <li className="!pb-4">
                    <a href="/mywallet" className="!text-primary">
                      My Wallet
                    </a>
                  </li>
                  <li className="!pb-4">
                    <a href="/myorders" className="!text-primary">
                      My Orders
                    </a>
                  </li>
                </ul>
              )}

              <h3 className="!pb-3">
                <a
                  href="/aboutus"
                  className="!font-semibold !text-lg !text-primary"
                >
                  ABOUT US
                </a>
              </h3>
              <h3 className="!pb-3">
                <a href="/blog" className="!font-semibold !text-lg !text-primary">
                  BLOG
                </a>
              </h3>
              <Link
                href="/signin"
                style={{ display: userData !== null ? 'flex' : 'none' }}
              >
                <div className="!flex !items-center !gap-2 !text-primary">
                  LogOut <FontAwesomeIcon icon={faRightFromBracket} />
                </div>
              </Link>
            </div>
          </div>

          {/* Overlay for Sidebar */}
          {isOpen && (
            <div
              className="!fixed !top-0 !left-0 !w-full !h-full !bg-[#00000092] !z-[999]"
              onClick={toggleSidebar}
            ></div>
          )}

          {/* Navbar Menu (Visible on xl and above) */}
          <div className="!hidden xl:!flex !w-4/12 !justify-start">
            <ul className="!flex !gap-4 !items-center">
              {mainCategories.slice(0, 5).map((mainCategory) => (
                <li key={mainCategory.id} className="!relative !group">
                  <a
                    href={`/products/${mainCategory.seo_url}`}
                    className="!text-primary !text-nowrap !flex !items-center !gap-1 !font-normal"
                  >
                    {mainCategory.name}
                    {getSubcategoriesByParentId(mainCategory.id).length > 0 && (
                      <FaChevronDown className="!text-sm" />
                    )}
                  </a>
                  <ul className="!absolute !hidden group-hover:!block !bg-white !shadow-lg !min-w-[200px] !mt-1 !z-50">
                    {getSubcategoriesByParentId(mainCategory.id).map(
                      (subCategory) => (
                        <li key={subCategory.id} className="!relative !group/sub">
                          <a
                            href={`/products/${subCategory.seo_url}`}
                            className="!block !px-4 !py-2 !text-primary !flex !items-center !justify-between !hover:bg-gray-100"
                          >
                            {subCategory.name}
                            {getSubcategoriesByParentId(subCategory.id).length >
                              0 && <FaChevronDown className="!text-sm" />}
                          </a>
                          <ul className="!absolute !hidden group-hover/sub:!block !bg-white !shadow-lg !min-w-[200px] !left-full !top-0">
                            {getSubcategoriesByParentId(subCategory.id).map(
                              (subSubCategory) => (
                                <li
                                  key={subSubCategory.id}
                                  className="!relative !group/subsub"
                                >
                                  <a
                                    href={`/products/${subSubCategory.seo_url}`}
                                    className="!block !px-4 !py-2 !text-primary !hover:bg-gray-100"
                                  >
                                    {subSubCategory.name}
                                  </a>
                                  <ul className="!absolute !hidden group-hover/subsub:!block !bg-white !shadow-lg !min-w-[200px] !left-full !top-0">
                                    {getSubcategoriesByParentId(
                                      subSubCategory.id,
                                    ).map((subSubSubCategory) => (
                                      <li key={subSubSubCategory.id}>
                                        <a
                                          href={`/products/${subSubSubCategory.seo_url}`}
                                          className="!block !px-4 !py-2 !text-primary !hover:bg-gray-100"
                                        >
                                          {subSubSubCategory.name}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </li>
                              ),
                            )}
                          </ul>
                        </li>
                      ),
                    )}
                  </ul>
                </li>
              ))}
              <li
                className={`!text-primary ${selected === 'contact-us' ? '!font-bold' : ''}`}
              >
                <a
                  href="/contactus"
                  className="!text-primary !whitespace-nowrap !uppercase"
                  onClick={() => handleSelect('contact-us')}
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Logo */}
          <div className="!w-4/12 !flex !justify-center">

            <Link href="/" className="flex items-center gap-2 p-2">
              <img
                className=""
                src={`${getLogo()}`}
                alt={`${getStoreName()} Logo`}
                width={160}
                height={50}
              />
            </Link>
          </div>

          {/* Icons */}
          <div className="!w-4/12 !flex !justify-end !items-center !gap-4">
            <div className="!cursor-pointer" onClick={toggleSearchBar}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M21 21L15.803 15.803M15.803 15.803C17.2096 14.3965 17.9998 12.4887 17.9998 10.4995C17.9998 8.51035 17.2096 6.60262 15.803 5.19605C14.3964 3.78947 12.4887 2.99927 10.4995 2.99927C8.51029 2.99927 6.60256 3.78947 5.19599 5.19605C3.78941 6.60262 2.99921 8.51035 2.99921 10.4995C2.99921 12.4887 3.78941 14.3965 5.19599 15.803C6.60256 17.2096 8.51029 17.9998 10.4995 17.9998C12.4887 17.9998 14.3964 17.2096 15.803 15.803Z"
                  stroke="#555555"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* User Icon and Dropdown */}
            <div className="!relative group !hidden md:!block">
              {userData === null ? (
                // Signin link if user not logged in
                <Link href="/signin" className="!flex !items-center !cursor-pointer group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="!text-[#555555] group-hover:!text-primary !transition-all !duration-300"
                  >
                    <path
                      d="M15.75 6C15.75 6.99456 15.3549 7.94839 14.6516 8.65165C13.9484 9.35491 12.9945 9.75 12 9.75C11.0054 9.75 10.0516 9.35491 9.34833 8.65165C8.64506 7.94839 8.24998 6.99456 8.24998 6C8.24998 5.00544 8.64506 4.05161 9.34833 3.34835C10.0516 2.64509 11.0054 2.25 12 2.25C12.9945 2.25 13.9484 2.64509 14.6516 3.34835C15.3549 4.05161 15.75 5.00544 15.75 6ZM4.50098 20.118C4.53311 18.1504 5.33731 16.2742 6.74015 14.894C8.14299 13.5139 10.0321 12.7405 12 12.7405C13.9679 12.7405 15.857 13.5139 17.2598 14.894C18.6626 16.2742 19.4668 18.1504 19.499 20.118C17.1464 21.1968 14.5881 21.7535 12 21.75C9.32398 21.75 6.78398 21.166 4.50098 20.118Z"
                      stroke="#555555"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              ) : (
                // Logged in user dropdown
                <>
                  <div
                    className="!flex !items-center !gap-2 !cursor-pointer group"
                    onClick={() => toggleDropdown()}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="!text-[#555555] group-hover:!text-primary !transition-all !duration-300"
                    >
                      <path
                        d="M15.75 6C15.75 6.99456 15.3549 7.94839 14.6516 8.65165C13.9484 9.35491 12.9945 9.75 12 9.75C11.0054 9.75 10.0516 9.35491 9.34833 8.65165C8.64506 7.94839 8.24998 6.99456 8.24998 6C8.24998 5.00544 8.64506 4.05161 9.34833 3.34835C10.0516 2.64509 11.0054 2.25 12 2.25C12.9945 2.25 13.9484 2.64509 14.6516 3.34835C15.3549 4.05161 15.75 5.00544 15.75 6ZM4.50098 20.118C4.53311 18.1504 5.33731 16.2742 6.74015 14.894C8.14299 13.5139 10.0321 12.7405 12 12.7405C13.9679 12.7405 15.857 13.5139 17.2598 14.894C18.6626 16.2742 19.4668 18.1504 19.499 20.118C17.1464 21.1968 14.5881 21.7535 12 21.75C9.32398 21.75 6.78398 21.166 4.50098 20.118Z"
                        stroke="#555555"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {/* <span className="!text-primary !text-sm">{userData.name}</span> */}
                  </div>

                  <div
                    className={`!absolute !bg-white !shadow-lg !min-w-[150px] !-right-3 !z-50  md:!hidden ${isDropdownOpen ? '!block' : '!hidden'
                      } md:group-hover:!block`}
                  >

                    <Link
                      href="/my-account"
                      className="!block !px-4 !py-2 !text-primary hover:!bg-gray-100"
                      onClick={() => handleNavigate('/myaccount')}
                    >
                      My Account
                    </Link>

                    <hr className="!border-t !border-gray-300" />
                    <Link
                      href="/signin"
                      className="!block !px-4 !py-2 !text-primary hover:!bg-gray-100"
                      onClick={() => handleNavigate('/signin')}
                    >
                      Logout
                    </Link>
                  </div>
                </>
              )}
            </div>



            <div className="!hidden md:!block">
              <Link
                href={!wishlist ? '/my-wishlist' : '/products/graphic-design-mens'}
                className="!hover:text-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill={wishlist ? '#f45440' : 'none'}
                >
                  <path
                    d="M21 8.25C21 5.765 18.901 3.75 16.312 3.75C14.377 3.75 12.715 4.876 12 6.483C11.285 4.876 9.623 3.75 7.687 3.75C5.1 3.75 3 5.765 3 8.25C3 15.47 12 20.25 12 20.25C12 20.25 21 15.47 21 8.25Z"
                    stroke="#555555"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>

            <div className="!relative">
              <Link href="/cart" className="!flex !items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M15.75 10.5V6C15.75 5.00544 15.3549 4.05161 14.6516 3.34835C13.9484 2.64509 12.9945 2.25 12 2.25C11.0054 2.25 10.0516 2.64509 9.34833 3.34835C8.64507 4.05161 8.24998 5.00544 8.24998 6V10.5M19.606 8.507L20.869 20.507C20.939 21.172 20.419 21.75 19.75 21.75H4.24998C4.09219 21.7502 3.93613 21.7171 3.79193 21.6531C3.64774 21.589 3.51863 21.4953 3.41301 21.3781C3.30738 21.2608 3.2276 21.1227 3.17884 20.9726C3.13008 20.8226 3.11343 20.6639 3.12999 20.507L4.39399 8.507C4.42314 8.23056 4.55361 7.9747 4.76024 7.78876C4.96686 7.60281 5.23501 7.49995 5.51298 7.5H18.487C19.063 7.5 19.546 7.935 19.606 8.507ZM8.62498 10.5C8.62498 10.5995 8.58548 10.6948 8.51515 10.7652C8.44482 10.8355 8.34944 10.875 8.24998 10.875C8.15053 10.875 8.05515 10.8355 7.98482 10.7652C7.91449 10.6948 7.87498 10.5995 7.87498 10.5C7.87498 10.4005 7.91449 10.3052 7.98482 10.2348C8.05515 10.1645 8.15053 10.125 8.24998 10.125C8.34944 10.125 8.44482 10.1645 8.51515 10.2348C8.58548 10.3052 8.62498 10.4005 8.62498 10.5ZM16.125 10.5C16.125 10.5995 16.0855 10.6948 16.0152 10.7652C15.9448 10.8355 15.8494 10.875 15.75 10.875C15.6505 10.875 15.5551 10.8355 15.4848 10.7652C15.4145 10.6948 15.375 10.5995 15.375 10.5C15.375 10.4005 15.4145 10.3052 15.4848 10.2348C15.5551 10.1645 15.6505 10.125 15.75 10.125C15.8494 10.125 15.9448 10.1645 16.0152 10.2348C16.0855 10.3052 16.125 10.4005 16.125 10.5Z"
                    stroke="#555555"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {cartCount > 0 && (
                  <div className="!absolute !-top-2 !-right-2 !bg-[#dcdf00] !rounded-full !h-4 !w-4 !flex !justify-center !items-center">
                    <p className="!text-[10px] !text-primary !font-semibold">
                      {cartCount}
                    </p>
                  </div>
                )}
              </Link>
            </div>
          </div>

          {/* Search Bar */}
          {showSearchBar && (
            <>
              <div
                className="!fixed !top-0 !left-0 !w-full !h-full !bg-[#0000008f] !backdrop-blur-sm !z-[999]"
                onClick={toggleSearchBar}
              />
              <div
                className={`!fixed !w-full !left-0 !bg-white !shadow-lg !z-[1000] !transition-all !duration-300 !ease-in-out ${showSearchBar ? '!top-0' : '!-top-24'
                  } !flex !justify-center !items-center p-2 md:p-4`}
              >
                <form
                  onSubmit={handleSearchSubmit}
                  className="flex w-full mx-auto md:justify-center items-center gap-2"
                >
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    className="!text-gray-500 !cursor-pointer"
                    onClick={toggleSearchBar}
                  />
                  <SearchBar isMobile={false} onNavigate={handleNavigate} />
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;